import { createContext, useContext } from 'react';
import type { LBNodeData } from '@/proto/logiblocks/logiblocks';
import type { WorkflowRunNodeStatus } from '@/apis/logiblocksAPI';
import type { ValidationError } from '../fieldValidation';

/**
 * Context for LBNode components to communicate back to LBCanvas.
 *
 * React Flow renders custom nodes as isolated components that - by default -
 * cannot directly modify the parent graph state. This context provides
 * an interface for nodes to read state and trigger changes in LBCanvas.
 */
interface LBNodeUpdateContextType {
  /**
   * Updates the data of a specific LBNode in the ReactFlow graph.
   * @param nodeId - The unique identifier of the LBNode to update
   * @param updates - Partial LBNode data to merge with existing LBNode data
   */
  updateLBNodeData: (
    nodeId: string,
    updates: Readonly<Partial<LBNodeData>>
  ) => void;

  /**
   * Opens the customize panel for a specific LBNode.
   * @param nodeId - The unique identifier of the LBNode to customize
   */
  onOpenCustomizePanel?: (nodeId: string) => void;

  /**
   * Opens the per-attempt audit-trail drawer for a specific LBNode, while a
   * run is selected (see `runNodeStatuses`). Takes over the node's click
   * target from `onOpenCustomizePanel` in that mode.
   * @param nodeId - The unique identifier of the LBNode to inspect
   */
  onOpenRunNodeDetail?: (nodeId: string) => void;

  /**
   * Called when the add child button is clicked on a node.
   * @param nodeId - The parent node's ID
   * @param buttonRect - The bounding rect of the plus button (for positioning the picker)
   */
  onAddChildClick?: (nodeId: string, buttonRect: DOMRect) => void;

  /**
   * Called when the node delete button is clicked.
   * @param nodeId - The node to delete, including its descendants
   */
  onDeleteNode?: (nodeId: string) => void;

  /**
   * Whether the workflow is read-only (published).
   * When true, nodes cannot be edited.
   */
  isReadOnly: boolean;

  /**
   * Map of node IDs to their validation errors.
   * Nodes with validation errors will be highlighted in the UI.
   * Automatically recomputed whenever the graph structure (nodes or edges) changes.
   */
  validationErrors: Map<string, ValidationError[]>;

  /**
   * Per-node status for the selected run, keyed by node ID. A non-null map means
   * a run is selected and the graph should paint its outcome; null means the
   * Editor is not showing a run. An empty map is a run where no node executed.
   */
  runNodeStatuses: ReadonlyMap<string, WorkflowRunNodeStatus> | null;

  /**
   * The current workflow ID for context-aware API calls.
   */
  workflowId?: string;

  /**
   * The automation owner's email, for "Notify automation owner" on a Delay until event. Null
   * when the automation has no owner or the owner has no address; undefined when not known.
   */
  ownerEmail?: string | null;
}

export const LBNodeUpdateContext =
  createContext<LBNodeUpdateContextType | null>(null);

export const LBNodeUpdateProvider = LBNodeUpdateContext.Provider;

/**
 * Hook to access LBNode update functionality from within a workflow LogiBlock.
 * Must be used within a LBNodeUpdateProvider.
 *
 * @returns Object containing the updateLogiBlockData function
 * @throws Error if used outside of a LBNodeUpdateProvider
 */
export function useLBNodeUpdate() {
  const context = useContext(LBNodeUpdateContext);
  if (!context) {
    throw new Error(
      'useLBNodeUpdate must be used within a LBNodeUpdateProvider'
    );
  }
  return context;
}
